import * as React from 'react';

import { cn } from '@/lib/utils';
import { formatNombre } from '@/lib/formats';

/**
 * Compact time series with a reading layer.
 *
 * The chart it replaces drew a polyline and stopped there: no axis, no
 * labels, no hover — the exact value of any day was unreadable, which makes
 * a chart decoration rather than information. An HTML chart is interactive by
 * nature, so it ships with:
 *
 *   - a crosshair and tooltip on hover / keyboard focus;
 *   - the latest value direct-labelled (selectively — never a number on
 *     every point);
 *   - a visually-hidden table, so the series is reachable by screen reader
 *     and copyable, not locked inside an SVG.
 *
 * Text wears text tokens; the series colour lives on the mark alone.
 */

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const Sparkline = ({
  points = [],
  color = 'hsl(var(--chart-1))',
  label = 'Série',
  height = 64,
  width = 320,
  className,
  // Un montant de 570 000 F s'affichait « 570000 » : la valeur passe par
  // le format français (espaces des milliers), ou par celui de l'écran
  // appelant (montant en devise, litres…).
  formatValue = (v) => formatNombre(v),
  valueLabel = 'Nombre',
}) => {
  const [active, setActive] = React.useState(null);
  const wrapRef = React.useRef(null);

  if (!points.length) {
    return (
      <p className="text-xs text-muted-foreground/70">Aucune donnée</p>
    );
  }

  const max = Math.max(...points.map((p) => p.count), 1);
  const step = points.length > 1 ? width / (points.length - 1) : width;
  const padY = 6; // keeps the 2px stroke off the edges
  const yOf = (c) => padY + (1 - c / max) * (height - padY * 2);
  const xOf = (i) => i * step;

  const line = points.map((p, i) => `${xOf(i)},${yOf(p.count)}`).join(' ');
  const last = points[points.length - 1];
  const shown = active ?? points.length - 1;
  const shownPoint = points[shown];

  const onMove = (e) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    const ratio = (e.clientX - box.left) / box.width;
    const idx = Math.round(ratio * (points.length - 1));
    setActive(Math.max(0, Math.min(points.length - 1, idx)));
  };

  const total = points.reduce((s, p) => s + p.count, 0);

  return (
    <div className={cn('relative', className)}>
      <div
        ref={wrapRef}
        onMouseMove={onMove}
        onMouseLeave={() => setActive(null)}
        className="relative"
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          height={height}
          preserveAspectRatio="none"
          role="img"
          aria-label={`${label} : ${formatValue(total)} au total sur ${points.length} jours, maximum ${formatValue(max)}.`}
        >
          {/* Recessive baseline — the line is read against it. */}
          <line
            x1="0"
            y1={height - padY}
            x2={width}
            y2={height - padY}
            stroke="hsl(var(--border))"
            strokeWidth="1"
          />
          <polyline
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={line}
            vectorEffect="non-scaling-stroke"
          />
          {active !== null && (
            <line
              x1={xOf(active)}
              y1={0}
              x2={xOf(active)}
              y2={height}
              stroke="hsl(var(--muted-foreground))"
              strokeWidth="1"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
          <circle
            cx={xOf(shown)}
            cy={yOf(shownPoint.count)}
            r="3.5"
            fill={color}
            stroke="hsl(var(--card))"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>

      {/* One direct label, not a number on every point. */}
      <p className="mt-1 flex items-baseline gap-1.5 text-xs">
        <span className="font-mono tabular font-semibold text-foreground">
          {formatValue(shownPoint.count)}
        </span>
        <span className="text-muted-foreground">
          {active !== null ? fmtDate(shownPoint.date) : `au ${fmtDate(last.date)}`}
        </span>
      </p>

      {/* The series, reachable without seeing the drawing. */}
      <table className="sr-only">
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">{valueLabel}</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.date}>
              <th scope="row">{fmtDate(p.date)}</th>
              <td>{formatValue(p.count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
Sparkline.displayName = 'Sparkline';

export { Sparkline };
